import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, BookOpen, FileCheck2, Briefcase, Wrench, User, LogOut, Sun, Moon, Shield } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const navItems = [
  { to: "/dashboard", label: "Home", icon: Home, id: "home" },
  { to: "/learn", label: "Learn", icon: BookOpen, id: "learn" },
  { to: "/exams", label: "Exams", icon: FileCheck2, id: "exams" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, id: "jobs" },
  { to: "/tools", label: "Tools", icon: Wrench, id: "tools" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-9 w-9 rounded-xl bg-slate-900 dark:bg-amber-500 flex items-center justify-center">
              <Shield className="h-5 w-5 text-amber-400 dark:text-slate-950" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-base tracking-tight">HSE Career Hub</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">Learn · Certify · Get Hired</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                data-testid={`nav-${n.id}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button data-testid="theme-toggle" onClick={toggle} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user?.role === "admin" && (
              <button data-testid="admin-link" onClick={() => nav("/admin")} className="hidden md:inline-flex btn-outline !py-1.5 !px-3 text-xs">Admin</button>
            )}
            {user ? (
              <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Logout">
                <LogOut className="h-4 w-4" />
              </button>
            ) : (
              <Link to="/" data-testid="signin-btn" className="btn-primary !py-1.5 !px-3 text-xs">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 md:px-8 py-6 safe-bottom">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 z-50 glass border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-around">
        {navItems.map((n) => {
          const active = loc.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`bottom-nav-${n.id}`}
              className={`flex flex-col items-center justify-center px-2 py-1 min-w-[44px] min-h-[44px] ${active ? "text-amber-500" : "text-slate-500 dark:text-slate-400"}`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] mt-0.5 font-semibold">{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
